import { shouldCaptureValue } from '@posthog/browser-common/utils/autocapture-utils'

/** The bodies of a network request that session replay recorded. */
interface RecordedBodies {
  requestBody?: string | null
  responseBody?: string | null
}

// posthog-js's own list: a body containing any of these may carry a credential.
const CREDENTIAL_MARKERS = [
  'password',
  'secret',
  'passwd',
  'api_key',
  'apikey',
  'auth',
  'credentials',
  'mysql_pwd',
  'privatekey',
  'private_key',
  'token',
]

const REDACTED = '[redacted]'

/**
 * Blank the recorded bodies a session replay must not keep: any that may hold a credential, a card
 * number or a Social Security number, and requests that send a one-time sign-in code.
 *
 * posthog-js runs the first check itself only when an app sets no network mask, so use this as the
 * mask, or call it from one.
 */
export function scrubRecordedBodies<Request extends RecordedBodies>(request: Request): Request {
  return {
    ...request,
    requestBody: sendsSignInCode(request.requestBody) ? REDACTED : scrubBody(request.requestBody),
    responseBody: scrubBody(request.responseBody),
  }
}

function scrubBody(body: string | null | undefined): string | null | undefined {
  if (!body) return body
  const sensitive =
    !shouldCaptureValue(body, false) || CREDENTIAL_MARKERS.some((marker) => body.includes(marker))
  return sensitive ? REDACTED : body
}

/** Whether a JSON request body sends a sign-in code, which every code check posts as a top-level code field. */
function sendsSignInCode(body: string | null | undefined): boolean {
  if (!body) return false
  try {
    const parsed: unknown = JSON.parse(body)
    return typeof parsed === 'object' && parsed !== null && 'code' in parsed
  } catch {
    return false
  }
}
